import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import {
  WizardFlow,
  WizardFlowType,
  WizardState,
  WizardSuccessProps,
} from '@/features/wizard';
import {
  praiseWizardFlow,
  WizardStep,
  Employee,
  GoalImpact,
  PraiseFormData,
  WizardAnswers,
  praiseStepComponentMap,
  Interaction,
  PraiseInteraction,
} from '@momentum/types';
import {
  useEmployees,
  useCreatePraise,
  useMyGoals,
  useUpdatePraise,
} from '@/shared/hooks';
import {
  createFetchData,
  createPropsResolver,
  convertDomainStepToReactStep,
} from '@/routes/_dashboard/interactions/-components/WizardComponentUtils';
import { InteractionReviewStep } from './InteractionReviewStep';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { useValues } from '@/shared/hooks/useValues';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

interface PraiseWizardProps {
  // Optional pre-selected employee
  initialEmployeeId?: string;
  existingInteractionId?: string;
  // Optional custom completion handler (if not provided, will submit to API and navigate)
  onComplete?: (interaction?: Interaction) => void;
  // Optional initial state
  initialState?: Partial<WizardState>;
  // Wizard UI configuration
  showProgress?: boolean;
  showNavigation?: boolean;
  autoStart?: boolean;
  // Loading/error message translations (can be overridden)
  loadingMessage?: string;
  errorMessage?: string;
  firstStepId?: string;
  flowId?: string;
}

export const PraiseWizard = ({
  initialEmployeeId,
  existingInteractionId,
  onComplete,
  initialState: externalInitialState,
  showProgress = true,
  showNavigation = true,
  autoStart = true,
  loadingMessage,
  errorMessage,
  firstStepId,
  flowId = praiseWizardFlow.id,
}: PraiseWizardProps) => {
  const { t } = useTranslation('wizard');
  const { t: tCommon } = useTranslation('common');
  const employee = useEmployeeContext();
  const { data: employees, isLoading, error } = useEmployees({});
  const navigate = useNavigate();

  const {
    data: goals,
    isLoading: isLoadingGoals,
    error: errorLoadingGoals,
  } = useMyGoals(employee._id);
  const createPraiseMutation = useCreatePraise();
  const updatePraiseMutation = useUpdatePraise();

  const {
    data: companyValues,
    isLoading: isLoadingCompanyValues,
    error: errorLoadingCompanyValues,
  } = useValues(employee.companyId);

  const wizardFlow: WizardFlowType<PraiseFormData> = useMemo(() => {
    const fetchData = createFetchData(
      employees || [],
      goals || [],
      companyValues || []
    );
    const resolver = createPropsResolver(fetchData);

    const createInteraction = async (
      props: WizardSuccessProps<PraiseFormData>,
      isDraft: boolean
    ) => {
      // Prepare the data for the API call
      const employeeSelection = props.answers['employee-selection'] as Employee;
      const selectedEmployee = employees?.find(
        (p: Employee) => p._id === employeeSelection?._id
      );

      const goalSelection = props.answers['goal-selection'];
      const goalImpactRange = props.answers['goal-impact-range'];
      const companyValues = props.answers['company-values'];
      const additionalEffects = props.answers['additional-effects'];

      const praiseData = {
        companyId: employee.companyId,
        creatorId: employee._id,
        receiverId: selectedEmployee?._id || '',
        goal:
          goalSelection && goalSelection !== 'no-effect'
            ? {
                id: goalSelection,
                impact: goalImpactRange
                  ? goalImpactRange === 3
                    ? GoalImpact.HIGH
                    : goalImpactRange === 2
                    ? GoalImpact.MEDIUM
                    : GoalImpact.LOW
                  : undefined,
              }
            : null,
        companyValues: companyValues?.map((value: string) => ({ value })) || [],
        furtherImpact:
          additionalEffects?.map((effect: string) => ({
            id: effect,
            affected: ['recipient'], // Default to recipient, could be made more dynamic
          })) || [],
        formData: props.answers, // Pass the complete form data
        isDraft,
      };

      try {
        let praise: PraiseInteraction | undefined;
        if (existingInteractionId) {
          praise = await updatePraiseMutation.mutateAsync({
            id: existingInteractionId,
            data: praiseData,
          });
        } else {
          praise = await createPraiseMutation.mutateAsync(praiseData);
        }

        toast.success(t('praiseQuestions.praiseSubmittedSuccessfully'));

        if (onComplete) {
          onComplete(praise);
        } else {
          sessionStorage.removeItem(flowId);
          navigate({ to: '/' });
        }
      } catch (error) {
        toast.error(t('praiseQuestions.praiseSubmittedFailed'), {
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    };

    return {
      id: flowId,
      title: t('praiseQuestions.title'),
      description: t('praiseQuestions.description'),
      firstStepId: praiseWizardFlow.firstStepId,
      steps: praiseWizardFlow.steps.map((step) =>
        convertDomainStepToReactStep(
          step as WizardStep<PraiseFormData>,
          resolver,
          t,
          employee
        )
      ),
      successComponent: (props) => (
        <InteractionReviewStep
          {...props}
          answers={props.answers as WizardAnswers<PraiseFormData>}
          employees={employees || []}
          interactionType="praise"
          companyValues={companyValues || []}
          onSendInteraction={async () => {
            console.log('Sending praise with answers:', props.answers);
            await createInteraction(props, false);
          }}
          onSaveDraft={async () => {
            console.log('Sending DRAFT praise with answers:', props.answers);
            await createInteraction(props, true);
          }}
          isSubmitting={createPraiseMutation.isPending}
        />
      ),
      onComplete: (answers) => {
        // Just log completion, actual submission happens in the review component
        console.log('Praise Questions Wizard completed with answers:', answers);
      },
    };
  }, [t, employees, createPraiseMutation, goals, companyValues]);

  // Prepare initial state if employeeId is provided
  const initialState = useMemo<Partial<WizardState>>(() => {
    if (externalInitialState) {
      return externalInitialState;
    }

    if (initialEmployeeId && employees && employees.length > 0) {
      const selectedEmployee = employees.find(
        (emp: Employee) => emp._id === initialEmployeeId
      );
      if (selectedEmployee) {
        return {
          answers: {
            'employee-selection': selectedEmployee,
          },
        };
      }
    }
    return {};
  }, [initialEmployeeId, employees, externalInitialState]);

  // Handle loading and error states
  // Wait for employees to load if employeeId is provided
  if (
    isLoading ||
    isLoadingGoals ||
    isLoadingCompanyValues ||
    (initialEmployeeId && !employees)
  ) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">
            {loadingMessage ?? tCommon('loadingData')}
          </div>
        </div>
      </div>
    );
  }

  if (error || errorLoadingGoals || errorLoadingCompanyValues) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">
            {errorMessage ?? tCommon('errorLoadingData')}{' '}
            {error?.message ||
              errorLoadingGoals?.message ||
              errorLoadingCompanyValues?.message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <WizardFlow
      flow={wizardFlow}
      showNavigation={showNavigation}
      showProgress={showProgress}
      autoStart={autoStart}
      initialState={initialState}
      firstStepId={firstStepId}
      reason={{
        title: t('stepTitles.reason-details'),
        steps: Object.keys(praiseStepComponentMap).filter(
          (key) =>
            ![
              'reason-selection',
              'date-selection',
              'employee-selection',
              'goal-selection',
              'goal-impact-range',
              'company-values',
              'additional-effects',
              'additional-effects-details',
            ].includes(key)
        ) as (keyof PraiseFormData)[],
        afterStep: 'reason-selection',
      }}
    />
  );
};
